import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Alert,
  Box,
  Button,
  ChevronLeftIcon,
  type TabProps,
  Tabs,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useOutletContext } from "react-router-dom";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { UserSummary } from "@dealer-center/ddc-pages-configuration-interface";
import {
  useDeleteUserMutation,
  useGetAccountAccess,
  useGetUserInfo,
  useOperatorAuthority
} from "@dealer-center/ddc-pages-configuration-fetch";
import {
  AccountAccessTab,
  BORDER_MUTED,
  FeatureAccessTab,
  IdentityHeader,
  LoginHistoryTab,
  SPACE_BLOCK,
  SPACE_SECTION,
  SURFACE_DEFAULT,
  SURFACE_MUTED,
  StatusRegion,
  UnsavedChangesPrompt,
  UserInfoAreaContext,
  type UserInfoTabId,
  UserInformationTab,
  belowMobile,
  hasAccountHierarchy,
  isMobileViewport,
  mobileOnly,
  usePendingEditsGuard,
  useTabScrollButtons,
  useUserInfoEdits,
  useUserInfoSave,
  useUserInfoTabs
} from "../components/UserInfo";
import { AddUserModal } from "./components/AddUser/AddUserModal";
import { HeaderActions } from "./components/HeaderActions";
import { PageHeading } from "./components/PageHeading";
import { SubjectStatusBridge } from "./components/SubjectStatusBridge";
import { UserListPanel } from "./components/UserList/UserListPanel";
import { useDrillDownView } from "./useDrillDownView";
import { type UserListSortOption, useUserListQuery } from "./useUserListQuery";
import { useUserManagerSubject } from "./useUserManagerSubject";
import { deleteErrorMessage, isDeleteNotFound } from "./writeErrorMessages";

const TABS_ID = "usermanager-tabs";

/**
 * How the shared panel attributes its telemetry while this page renders it.
 *
 * The slug matches the `usermanager-*` names this page's own controls already use
 * (`HeaderActions`, `ListToolbar`, `ListPager`), so the panel's events and the shell's land in one
 * bucket rather than two. Before this provider the panel reported itself as **My Info** — a real
 * mis-attribution in New Relic and GTM, not a cosmetic one (review 2026-09-22).
 *
 * Module scope, so its identity is stable and the provider never re-renders its subtree for a new
 * object.
 */
const USER_MANAGER_AREA = { label: "User Manager", slug: "usermanager" };

export interface UserManagerOutletContext {
  ccUserInfo?: ControlcenterAPIInterfaceExternal;
}

/**
 * The User Manager page shell: a user list beside the shared detail panel.
 *
 * ## The invariant this shell exists to hold
 *
 * **The outlet context is read exactly once, here, and both identities are passed down as props.**
 * That is `MyInfo.tsx`'s invariant, and it is more load-bearing here than there: this page holds
 * **two** identities at once — the **operator** (whose own records supply authority) and the
 * **subject** (the selected row) — and they are usually different people. No hook below this point
 * may re-read the outlet context or re-resolve either one. Two readers could drift, and drifting
 * here means showing one user's header over another's data, or deciding a control's enabled state
 * from the wrong person's permissions.
 *
 * ## FR-005's flex chain
 *
 * The page root and both panels are `display: flex` columns with `minHeight: 0`, and there are
 * **exactly two scrollers**: the user-rows box (inside `UserListPanel`) and `[role='tabpanel']`.
 * Each gets `flex: 1 1 auto; overflow-y: auto`; everything else is `flexShrink: 0`. A single missing
 * `minHeight: 0` anywhere in the chain lets a child grow instead of scroll and the **whole page**
 * scrolls as one block — the exact defect FR-005 exists to prevent.
 *
 * The `[role='tabpanel']` treatment is the shipped mechanism, reused rather than reinvented:
 * Interstate renders the tab bar and each panel as flat siblings with no height or flex styling and
 * offers no prop for it, so the wrapper must both establish the column and hand the panel the
 * remaining space. It also zeroes Interstate's per-panel `margin-left: 8px; margin-top: 16px`,
 * targeted **by `role`** rather than by class name so it survives an Interstate class-hash change.
 *
 * **jsdom cannot verify any of this** — it computes no layout, so `scrollHeight`/`clientHeight` are
 * `0` and `overflow` is never resolved. T097 covers it in Cypress; a Jest assertion here would only
 * re-assert the `sx` object.
 */
export const UserManager = () => {
  const context = useOutletContext<UserManagerOutletContext | null>();
  const operatorUserId = context?.ccUserInfo?.userId;
  const accountId = context?.ccUserInfo?.accountId;

  /*
   * FR-058's narrow carve-out, resolved ONCE. Both of its reads are self-requests against the
   * operator's own identifiers — which is the entire justification for reading a display-only
   * response as authority. See `useOperatorAuthority`.
   */
  const authority = useOperatorAuthority({
    operatorUserId,
    accountId,
    /*
     * `getCurrent()`, not `getLocale()` — the rule stated in `LoginHistoryTab`. `getLocale()`
     * re-derives the locale from `?lang=`/`navigator.language` and **ignores `Locale.change()`**,
     * so it reports the source the app started from rather than the locale in effect.
     *
     * It also has to match `FeatureAccessTab`'s `getCurrent()` exactly: `useGetFeatureAccess` keys
     * on `locale`, so two different values are two cache entries. When the operator selects their
     * own record the subject's read and this self-read are meant to collapse to **one** entry — the
     * behavior `useOperatorAuthority`'s "cache separation is free" note asserts — and a mismatched
     * locale silently split them into a duplicate request against the slowest endpoint on the page.
     */
    locale: Locale.getCurrent()
  });

  const list = useUserListQuery({ accountId });
  const { goToPage: goToListPage } = list;

  /*
   * Which pane the mobile drill-down shows, held in `?view=` so the hardware Back button works.
   * Inert at desktop, where both panes are visible. **Reads the view, never the selected user** —
   * FR-006/D-07 forbid the latter; see the hook.
   */
  const { showDetail, showList, view } = useDrillDownView();

  /*
   * T048 / R-008. `selectedUserId` is page state — **not in the URL** (FR-006, D-07) and **not
   * derived from the current page's rows**.
   *
   * Deriving it would silently reselect on every keystroke and discard pending edits, which FR-017
   * and SC-007 forbid outright: the selection must survive a filter, sort or page change **even when
   * the change removes the selected user from the visible rows**, prompting for nothing.
   *
   * The row's summary is cached **alongside** the id rather than looked up in the current page, for
   * the same reason — the selected user may not be in `list.query.data.users` at all. It feeds the
   * identity fallback and the unsaved-changes prompt.
   */
  const [selectedUser, setSelectedUser] = useState<UserSummary | undefined>();

  /*
   * The default-to-first-row rule applies on **first render only**, never on a refetch (FR-017,
   * SC-007). The ref is what makes "once" true: it must not itself trigger a render, and it must
   * **not** reset when the list refetches.
   *
   * Deliberately NOT `selectedUser ?? firstUser`. That reads like the same thing and is the exact
   * failure FR-017 forbids — it would re-derive on every render, so any refetch that reordered the
   * rows would silently move the selection and discard pending edits.
   */
  const hasAppliedInitialSelection = useRef(false);
  const firstUser = list.query.data?.users?.[0];

  useEffect(() => {
    if (!hasAppliedInitialSelection.current && firstUser) {
      hasAppliedInitialSelection.current = true;
      setSelectedUser(firstUser);
    }
  }, [firstUser]);

  const { subject, unavailableReason } = useUserManagerSubject(
    selectedUser,
    accountId
  );

  /*
   * Pending User Information edits, owned **here** rather than in the tab — the same reason My Info
   * gives: the panel handed to an inactive tab is an empty fragment, so `UserInformationTab`
   * unmounts on every tab switch and state held inside it would vanish the moment the operator
   * opened Login History. Owning it at the page level is what makes FR-022's guarantee hold.
   */
  const userInfoEdits = useUserInfoEdits();

  // Announced by `SubjectStatusBridge` after the selection change, so the change does not wipe it.
  const deleteUser = useDeleteUserMutation();
  const { removeDeletedUserQueries } = deleteUser;
  const { toastError, toastSuccess } = useToaster();
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState<UserSummary | undefined>();
  // Where focus goes once a write's dialog is gone (FR-076); its opener may be unmounted or hidden.
  const [focusAfterWrite, setFocusAfterWrite] = useState<
    "create" | "delete" | undefined
  >();
  const [pendingAnnouncement, setPendingAnnouncement] = useState<
    string | undefined
  >();
  const [deletedUser, setDeletedUser] = useState<
    { accountId: string; userId: string } | undefined
  >();
  const handleAnnounced = useCallback(() => {
    setPendingAnnouncement(undefined);
  }, []);

  /*
   * The subject's profile, read **here** so the guard can ask whether the form is dirty —
   * `isDirty` compares the edit overrides against the stored record, and this shell owns the edits
   * but not the record. My Info reads it at its shell for the same reason. The cost is zero — React
   * Query dedupes against the tab's identical per-`userId` key, so this adds no request.
   */
  const subjectInfo = useGetUserInfo({
    userId: subject?.userId,
    accountId
  });

  /*
   * FR-072's conditional Account Access tab. The query lives **here**, not in the tab, so the tab
   * strip renders before it resolves — `accountaccess` is ~5–6s upstream and gating the strip on it
   * would leave the panel empty for seconds.
   *
   * The gate is `isSuccess`, **not** `!isLoading`: on **failure** the tab stays, because a failed
   * read tells us nothing about whether a hierarchy exists and removing the tab would report "this
   * user has no account hierarchy" — inventing an answer from an absent response. A single-account
   * user briefly sees a tab that then disappears; accepted deliberately.
   *
   * **Re-determined per selected user.** React Query's per-`userId` key makes each determination
   * independent and cached, so re-selecting a user costs no second request.
   */
  const accountAccess = useGetAccountAccess({
    userId: subject?.userId,
    accountId
  });
  const isAccountAccessUnavailable =
    accountAccess.isSuccess && !hasAccountHierarchy(accountAccess.data);

  const { tabs, activeTabId, selectTab } = useUserInfoTabs({
    unavailableTabIds: isAccountAccessUnavailable ? ["account"] : []
  });

  // A write can resolve after the operator selected someone else; its outcome must not apply to them.
  const selectedUserIdRef = useRef(subject?.userId);

  useEffect(() => {
    selectedUserIdRef.current = subject?.userId;
  }, [subject?.userId]);

  /*
   * ⚠️ **Called HERE, above the three early returns below — not down beside the `Tabs` element.**
   *
   * It is a hook, so the no-context / pending / no-access branches must not skip it. That means the
   * effect first runs with **no tab strip in the DOM at all**, and the strip later unmounts and
   * remounts as a new node every time the selection goes empty and comes back. The hook resolves
   * its root by `id` on each mutation specifically to survive that — see its docstring before
   * "simplifying" it to capture the element once.
   *
   * The arrows are load-bearing here rather than the redundant affordance they are on My Info: this
   * strip shares its row with the user-list rail, so four tabs overflow at ordinary desktop widths.
   * This page previously hid them outright with an `sx` rule; do not restore it.
   */
  useTabScrollButtons(TABS_ID);

  /*
   * FR-009 / FR-055, as **ONE comparison** — not a flag threaded through the tree.
   *
   * When the subject is the operator, the permission tabs receive **no `authority`** and therefore
   * render read-only, and the notice explains why. SC-005's criterion is zero paths by which a user
   * can change their own access permissions through this page; one comparison at the shell is a
   * smaller surface for that to go wrong than a boolean passed through four components.
   */
  const isSelfSelected = Boolean(
    subject && operatorUserId && subject.userId === operatorUserId
  );
  const permissionAuthority = isSelfSelected ? undefined : authority.data;

  // The save is shared with My Info; `HeaderActions` announces its success.
  const save = useUserInfoSave({
    userId: subject?.userId,
    accountId,
    summary: subjectInfo.data?.summary,
    edits: userInfoEdits,
    activeTabId,
    selectTab,
    isSubjectTheOperator: isSelfSelected
  });
  const { clearSaveState } = save;

  /*
   * FR-023 and FR-024. Note what is NOT routed through this guard: filter, sort and page changes.
   * FR-017 requires those to prompt for nothing and discard nothing, so they call the list hook
   * directly. `isFormDirty` is derived once, by the save hook, for both this and Save's `disabled`
   * state, so the two can never disagree about whether there are edits.
   */
  const pendingEdits = usePendingEditsGuard({
    isDirty: save.isFormDirty,
    isSaving: save.isSaving,
    onDiscard: userInfoEdits.reset
  });

  /*
   * **The selection is scoped to the account, so a changed `accountId` clears it.**
   *
   * This does **not** contradict FR-017's rule above that the latch must never reset on a refetch.
   * FR-017 is about the *same* account's rows being re-fetched, re-sorted or re-filtered — where
   * keeping the selection is the requirement. A different **account** is a different set of users
   * entirely: keeping `selectedUser` there would leave the previous account's subject on screen and
   * read its record against the new account, and the lowered latch is what lets the
   * default-to-first-row rule apply again to the new account's rows.
   *
   * ⚠️ **The previous-`accountId` ref is what makes this safe on mount, and it is NOT a substitute
   * for a dependency array.** Both this effect and the initial-selection effect above run on mount,
   * in declaration order, and a bare `[accountId]` effect would therefore fire *after* the selection
   * was applied and wipe it — the page rendered with no subject at all. Comparing against the
   * previous value makes the mount run a genuine no-op, so the two effects cannot fight whichever
   * order they end up declared in. (Cost me the first cut of this fix, 2026-09-22.)
   *
   * ⚠️ **Depend on `userInfoEdits.reset`, never on `userInfoEdits`.** That object is memoized on
   * `valueOf`/`isDirty`, which close over the edits, so its identity changes on **every keystroke** —
   * depending on it would wipe the selection as the operator typed. `reset` is a `useCallback` with
   * no deps and is stable for the life of the page.
   *
   * Latent hardening, recorded as such: no in-app path changes the as-account today (nothing links to
   * `/cc/as/...`, so it takes a full document load, which remounts this component anyway). Added in
   * review 2026-09-22 so an account switcher cannot land this defect silently.
   */
  const previousAccountIdRef = useRef(accountId);

  useEffect(() => {
    if (previousAccountIdRef.current === accountId) {
      return;
    }

    previousAccountIdRef.current = accountId;
    hasAppliedInitialSelection.current = false;
    setSelectedUser(undefined);
    userInfoEdits.reset();
    clearSaveState();
  }, [accountId, userInfoEdits.reset, clearSaveState]);

  /*
   * FR-001a. The criterion is the PERMISSION, not internal status — see the gate below.
   *
   * Read from `authority.pageAccess`, **not** `authority.data`. `data` requires the account-access
   * self-read as well, so gating on it turned any failure of that ~5–6s read into a "you don't have
   * access" denial for an operator who does hold the permission. See `useOperatorAuthority`.
   */
  const isPageAvailable = authority.pageAccess.hasPermission;

  /*
   * Named `handle*` per the repo's `react/jsx-handler-names` rule, which requires a handler passed
   * to an `on*` prop to be a `handle*` function rather than a bare method reference.
   */
  /*
   * T087. FR-023 fires **before** `selectedUser` changes, never after.
   *
   * `requestAction` runs the callback immediately when the form is clean and defers it behind the
   * prompt when it is dirty. Firing the selection first and asking afterwards would load the new
   * subject and *then* ask about the previous one's edits — which is precisely the data loss the
   * requirement exists to prevent.
   */
  /*
   * Mobile only, and it does **not** go through `requestAction`: returning to the list loses
   * nothing, because both panes stay mounted and the edits live on this shell. FR-023's prompt is
   * for a change of *subject*, which this is not. See `useDrillDownView`'s table.
   */
  const handleBackToList = () => {
    showList();
  };

  const handleSelectUser = (user: UserSummary) => {
    /*
     * ⚠️ **`reset()` here is load-bearing, and "the form is clean" does not make it redundant.**
     *
     * `requestAction` runs the action immediately when the form is clean, so `onDiscard` — the other
     * caller of `reset` — never fires on that path. But **clean is not the same as untouched**:
     * `isDirty` compares overrides against the record, so typing into an empty field and deleting it
     * again leaves `edits.fax === ""` while reporting clean. Those overrides are keyed by **field
     * name only, never by user**, so the stale `""` then blanked the *next* user's real fax number —
     * exactly the cross-user bleed `useUserInfoEdits.reset`'s own comment calls "a data-integrity
     * bug, not a cosmetic one" (found in review 2026-09-15; only the dirty→discard path was tested).
     *
     * Resetting inside the action covers both paths: on the dirty path `onDiscard` has already run
     * and this is a harmless no-op, and on the clean path it is the only thing that clears them.
     * It belongs here rather than in `usePendingEditsGuard`, which deliberately owns neither the
     * edits nor the record.
     */
    pendingEdits.requestAction(() => {
      userInfoEdits.reset();
      clearSaveState();
      setSelectedUser(user);
      /*
       * The drill-down's forward step, and it belongs **inside** the deferred action, not beside it.
       * Outside, a dirty form would navigate to the detail pane and only then ask whether to
       * discard — showing the operator the new subject while the prompt asked about the old one's
       * edits, which is the same ordering mistake the `requestAction` wrapper exists to prevent.
       *
       * No-ops at desktop, where both panes are already visible (`useDrillDownView.showDetail`).
       */
      showDetail();
    });
  };

  // Prompts for unsaved edits first, so selecting the created user discards nothing silently.
  const handleAddUser = () => {
    pendingEdits.requestAction(() => {
      setIsAddUserOpen(true);
    });
  };

  const handleAddUserClose = () => {
    setIsAddUserOpen(false);
  };

  // Closing the modal unmounts its form, which discards the password.
  const handleUserCreated = (user: UserSummary) => {
    setIsAddUserOpen(false);
    userInfoEdits.reset();
    clearSaveState();
    // A first list read that lands later must not replace the new user with row 0.
    hasAppliedInitialSelection.current = true;
    // The new user is selected by the effect below, once User Information is the active tab.
    setCreatedUser(user);
    selectTab("userinfo");
  };

  // Waits for the tab switch to commit, so no other tab's "Loading" speaks over the success (FR-077).
  useEffect(() => {
    if (!createdUser || activeTabId !== "userinfo") {
      return;
    }

    const text = Locale.getText("UM_ADD_SUCCESS") as string;
    setCreatedUser(undefined);
    setSelectedUser(createdUser);
    // The cache write adds the new user to page 0 only.
    goToListPage(0);
    showDetail();
    setFocusAfterWrite("create");
    setPendingAnnouncement(text);
    toastSuccess(text, "");
  }, [activeTabId, createdUser, goToListPage, showDetail, toastSuccess]);

  // The deleted user's cached reads are removed by the effect below, once the selection has cleared.
  const finishDelete = (
    deleted: { accountId: string; userId: string },
    messageKey: "UM_DEL_SUCCESS" | "UM_DEL_ERR_NOT_FOUND"
  ) => {
    const text = Locale.getText(messageKey) as string;

    userInfoEdits.reset();
    clearSaveState();
    setSelectedUser(undefined);
    showList();
    setFocusAfterWrite("delete");
    setDeletedUser(deleted);
    setPendingAnnouncement(text);
    if (messageKey === "UM_DEL_SUCCESS") {
      toastSuccess(text, "");
    } else {
      toastError(text);
    }
  };

  /** Resolves the failure copy to keep the confirmation open; `NOT_FOUND` is treated as deleted. */
  const handleDelete = async (): Promise<string | undefined> => {
    if (!subject || !accountId) {
      return undefined;
    }

    const deleted = { accountId, userId: subject.userId };
    try {
      await deleteUser.mutateAsync(deleted);
    } catch (error) {
      if (isDeleteNotFound(error)) {
        finishDeleteFor(deleted, "UM_DEL_ERR_NOT_FOUND");
        return undefined;
      }
      if (selectedUserIdRef.current !== deleted.userId) {
        const text = deleteErrorMessage(error);
        setPendingAnnouncement(text);
        toastError(text);
        return undefined;
      }
      return deleteErrorMessage(error);
    }

    finishDeleteFor(deleted, "UM_DEL_SUCCESS");
    return undefined;
  };

  // If the selection moved on mid-delete, leave the new subject alone: remove the reads, announce and toast.
  const finishDeleteFor = (
    deleted: { accountId: string; userId: string },
    messageKey: "UM_DEL_SUCCESS" | "UM_DEL_ERR_NOT_FOUND"
  ) => {
    if (selectedUserIdRef.current === deleted.userId) {
      finishDelete(deleted, messageKey);
      return;
    }

    void removeDeletedUserQueries(deleted);
    const text = Locale.getText(messageKey) as string;
    setPendingAnnouncement(text);
    if (messageKey === "UM_DEL_SUCCESS") {
      toastSuccess(text, "");
    } else {
      toastError(text);
    }
  };

  /*
   * Remove the deleted user's reads only once no subject is selected. Removing them earlier lets the
   * mounted `useGetUserInfo` refetch, get a 404, and flash the "can't be loaded" state.
   */
  useEffect(() => {
    if (!deletedUser || subject) {
      return;
    }

    void removeDeletedUserQueries(deletedUser);
    setDeletedUser(undefined);
  }, [deletedUser, removeDeletedUserQueries, subject]);

  // Runs after the dialog's focus restore, whose opener is now unmounted (delete) or hidden (mobile create).
  useEffect(() => {
    if (!focusAfterWrite) {
      return;
    }
    // Above mobile the create dialog's own restore already lands on Add User, which stays visible.
    if (focusAfterWrite === "create" && !isMobileViewport()) {
      setFocusAfterWrite(undefined);
      return;
    }
    // `showDetail` commits a render later, and Back can't take focus while its pane is still hidden.
    if (focusAfterWrite === "create" && view !== "detail") {
      return;
    }

    const testId =
      focusAfterWrite === "create"
        ? "usermanager-back-to-list"
        : isMobileViewport()
          ? "user-list-mobile-add-user"
          : "user-list-add-user";
    // The detail header mounts once the new subject resolves, so this re-runs until it is there.
    const target = document.querySelector<HTMLElement>(
      `[data-testid="${testId}"]`
    );
    if (target) {
      target.focus();
      setFocusAfterWrite(undefined);
    }
  }, [focusAfterWrite, view, subject]);

  const handleKeepEditing = () => {
    pendingEdits.cancel();
  };

  const handleDiscardEdits = () => {
    pendingEdits.confirmDiscard();
  };

  const handleFilterChange = (term: string) => {
    list.changeFilter(term);
  };

  const handleSortChange = (option: UserListSortOption) => {
    list.changeSort(option);
  };

  const handlePageChange = (nextPage: number) => {
    list.goToPage(nextPage);
  };

  /*
   * ⚠️ **The no-context case is answered BEFORE the permission gate, because the gate cannot be
   * evaluated at all without an operator identity.**
   *
   * With no ControlCenter outlet context there is no `operatorUserId` and no `accountId`, so every
   * query on the page is disabled and `hasPermission` can never resolve. Falling through to the gate
   * produced the wrong answer twice over: an indefinite "Loading" while `isPending` was read off a
   * disabled query (fixed in `useOperatorAuthority`), and then a **"you don't have access" denial**
   * — which blames the operator's permissions for what is a wiring error.
   *
   * FR-008 already specifies the right answer, and `useUserManagerSubject` already computes it:
   * report that the record could not be loaded. Bypassing the gate exposes nothing — with no
   * account and no user every read is disabled, so the branch below renders a message and no data.
   *
   * Found in review 2026-09-15. `UserManager.subject.spec.tsx`'s "reports a missing outlet context"
   * case asserted this state and passed, because it mocks `useOperatorAuthority` and so never met
   * the gate.
   */
  if (unavailableReason === "no-context") {
    return (
      <Box
        data-testid={"usermanager-subject-failed"}
        sx={{ padding: SPACE_SECTION }}
      >
        <Typography tag={"p"} variant={"body-md"}>
          {Locale.getText("UM_SUBJECT_FAILED") as string}
        </Typography>
      </Box>
    );
  }

  /*
   * FR-001a's gate. **"Being internal MUST NOT be the criterion"** — this reads the operator's User
   * Manager permission and nothing else.
   *
   * Dealer callers happen to be blocked upstream today regardless: the BFF rejects every
   * non-internal caller before dispatch. That is a **rollout state, not this design's behavior**
   * (D-14), so it is recorded here rather than relied on.
   *
   * Rendered only once the **permission** read has resolved — while it is pending the page shows a
   * centered spinner rather than flashing a denial at an operator who does hold the permission. It waits on
   * `pageAccess`, **not** on `authority.isPending`: the latter also waits for the ~5–6s
   * account-access self-read, which blanked the whole page — including the fast user list — for
   * seconds on every load.
   */
  if (authority.pageAccess.isPending) {
    return (
      <Box
        data-testid={"usermanager-authority-loading"}
        sx={{
          alignItems: "center",
          display: "flex",
          flex: "1 1 auto",
          justifyContent: "center",
          minHeight: 0,
          padding: SPACE_SECTION,
          textAlign: "center"
        }}
      >
        {/* Same spinner the user list and every tab use, so the gate reads as a load, not a blank page. */}
        <LoadingIndicator
          actionName={"usermanager-authority"}
          text={Locale.getText("analytics.LOADING") as string}
        />
      </Box>
    );
  }

  if (!isPageAvailable) {
    return (
      <Box
        data-testid={"usermanager-no-access"}
        sx={{ padding: SPACE_SECTION }}
      >
        <Alert role={"alert"} type={"error"}>
          {Locale.getText("NO_ACCESS") as string}
        </Alert>
      </Box>
    );
  }

  /*
   * All four panels are built; only the active one is handed real content. A JSX element is a
   * descriptor and mounts nothing, so this is what stops all four queries firing on page load —
   * including `loginhistory` at ~20s — for tabs the operator may never open.
   *
   * **`key={subject.userId}` on the three tabs that hold per-subject state — do not remove any.**
   * Both are the same defect: a tab reconciles by position, so without a key the component instance
   * survives a selection change and carries the previous user's state onto the next one.
   *
   * - **Login History** (T067): its page number is internal state, so selecting a user with fewer
   *   sign-ins while on page 3 would show an empty page that reads as "no history".
   * - **Feature Access** (found in review 2026-09-22): `PermissionGroup`'s `toggledByCode` is keyed
   *   by permission code and **never by user**, and its groups are keyed by `group.key`, which is
   *   stable across users — so the same instances survived and `toggledByCode[code] ?? row.isGranted`
   *   painted one user's toggles over another's real grants. The same cross-user bleed
   *   `useUserInfoEdits.reset()` exists to prevent on the User Information tab, and display-only only
   *   until the write endpoint lands.
   * - **Account Access**: each root `AccountNode` holds its subtree's unsaved toggles.
   */
  const panels: Record<UserInfoTabId, ReactNode> = subject
    ? {
        userinfo: (
          <UserInformationTab
            edits={userInfoEdits}
            errors={save.fieldErrorMessages}
            isReadOnly={save.isSaving}
            subject={subject}
          />
        ),
        /*
         * `isSubjectTheOperator` carries the FR-009/FR-055 comparison to the copy, so a tab showing
         * the operator's *own* record still says "your". It is deliberately a **separate** prop
         * from `authority`, even though both derive from `isSelfSelected` here: authority is also
         * withheld when the account-access self-read fails, and reusing its absence as a copy test
         * made an unrelated failure rewrite the wording (see `AccountAccessTabProps`).
         */
        account: (
          <AccountAccessTab
            authority={permissionAuthority}
            isSubjectTheOperator={isSelfSelected}
            key={subject.userId}
            subject={subject}
          />
        ),
        feature: (
          <FeatureAccessTab
            authority={permissionAuthority}
            key={subject.userId}
            subject={subject}
          />
        ),
        login: (
          <LoginHistoryTab
            isSubjectTheOperator={isSelfSelected}
            key={subject.userId}
            subject={subject}
          />
        )
      }
    : { userinfo: <></>, account: <></>, feature: <></>, login: <></> };

  const interstateTabs: TabProps[] = tabs.map(tab => ({
    active: tab.id === activeTabId,
    component: tab.id === activeTabId ? panels[tab.id] : <></>,
    id: tab.id,
    label: tab.label,
    value: tab.id
  }));

  return (
    <UserInfoAreaContext.Provider value={USER_MANAGER_AREA}>
      <Box
        data-testid={"usermanager-page"}
        sx={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: 0
        }}
      >
        {/*
         * FR-019a's subheading names the **signed-in operator** — not the as-user and not the selected
         * row. The whole context object is passed rather than picked-apart values because the three
         * fields it needs (`internalUserId`, `first`/`last`, `bridge_user_name`) are all
         * session-scoped, and keeping them together is what stops someone substituting the as-user's
         * `userId` for the operator's id. See `PageHeading` for the table of who renders where.
         */}
        <PageHeading ccUserInfo={context?.ccUserInfo} />

        <UnsavedChangesPrompt
          isOpen={pendingEdits.isPromptOpen}
          isSubjectTheOperator={isSelfSelected}
          onCancel={handleKeepEditing}
          onDiscard={handleDiscardEdits}
        />

        {/*
         * The prototype's `.pl-section`: **two independent surfaces side by side**, not one card with a
         * divider.
         *
         * It shipped as a single bordered card wrapping both, which is why the rail never looked like
         * the approved design — `contracts/user-list-panel.md` is explicit that the left rail is a
         * list with **no card**, and the prototype's border lives on `.um-rows` and `.um-detail`
         * separately. Corrected 2026-09-15. See `UserListPanel`.
         */}
        {/*
         * **Below `md` this is a drill-down, not a stack** (TA4236901). Both panes stay mounted and
         * CSS shows one:
         *
         * - `flexDirection` is a column so the visible pane takes the full width.
         * - `overflow` and `minHeight` relax, because below the breakpoint **FR-005's no-outer-scroll
         *   rule is deliberately dropped** and the page scrolls once. Nested scrollers a few rows
         *   tall in a ~600px-high viewport are the touch-scroll trap TA4236901 names; FR-005 is
         *   scoped to >= 680px rather than quietly violated.
         *
         * Keeping both panes mounted is what makes the drill-down cheap *and* correct: the detail
         * pane does not unmount when the operator goes back to the list, so pending edits survive
         * the trip (FR-022/FR-023), and FR-008's "neither panel blocks the other" holds by
         * construction.
         */}
        <Box
          data-testid={"usermanager-section"}
          sx={{
            alignItems: "stretch",
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "row",
            minHeight: 0,
            overflow: "hidden",
            ...belowMobile({
              flexDirection: "column",
              minHeight: "auto",
              overflow: "visible"
            })
          }}
        >
          {/*
           * The live region wraps both panes so it stays mounted when a delete clears the selection
           * and covers Add User. It renders a fragment, so the flex layout is unchanged.
           */}
          <StatusRegion>
            <SubjectStatusBridge
              pendingMessage={pendingAnnouncement}
              subjectUserId={subject?.userId}
              onAnnounced={handleAnnounced}
            />

            {accountId ? (
              <AddUserModal
                accountId={accountId}
                isOpen={isAddUserOpen}
                onClose={handleAddUserClose}
                onCreated={handleUserCreated}
              />
            ) : null}

            <UserListPanel
              filterInput={list.filterInput}
              filterTerm={list.filterTerm}
              isError={list.query.isError}
              isFiltered={list.isFiltered}
              isHiddenOnMobile={view === "detail"}
              isLoading={list.query.isPending}
              page={list.page}
              selectedUserId={selectedUser?.userId}
              sortOption={list.sortOption}
              totalCount={list.query.data?.totalCount ?? 0}
              totalPages={list.query.data?.totalPages ?? 0}
              users={list.query.data?.users ?? []}
              onAddUser={handleAddUser}
              onFilterChange={handleFilterChange}
              onPageChange={handlePageChange}
              onSelectUser={handleSelectUser}
              onSortChange={handleSortChange}
            />

            {/*
             * `.pl-main{flex:1 1 auto;min-width:0;padding:0 16px}` — the prototype's own CSS comment
             * calls this "the gutter", and it is what separates the rail from the card beside it now
             * that the two are no longer one surface.
             *
             * Below `md` the gutter is the wrong idea twice over: there is nothing beside the card to
             * be separated from, and 16px of each edge is a tenth of the viewport. So the horizontal
             * padding goes and the pane is hidden outright while the list is showing.
             */}
            <Box
              data-testid={"usermanager-detail-pane"}
              sx={{
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                padding: `0 ${SPACE_BLOCK}`,
                ...belowMobile({
                  /*
                   * The drill-down: at these widths only one pane shows, and `view` decides which.
                   * Above the boundary both are always visible, so this lives inside the query.
                   */
                  display: view === "detail" ? "flex" : "none",
                  minHeight: "auto",
                  padding: 0
                })
              }}
            >
              {/*
               * The detail panel — the prototype's `.um-detail` card, and FR-005's SECOND scroll region.
               * The identity row (with its actions) and the tab strip stay fixed; only the active tab's
               * content scrolls.
               */}
              <Box
                data-testid={"usermanager-detail-panel"}
                sx={{
                  backgroundColor: SURFACE_DEFAULT,
                  border: `1px solid ${BORDER_MUTED}`,
                  borderRadius: "8px",
                  display: "flex",
                  flex: "1 1 auto",
                  flexDirection: "column",
                  /* Relaxed below `md` so the page owns the scroll — see `usermanager-section`. */
                  minHeight: 0,
                  overflow: "hidden",
                  ...belowMobile({ minHeight: "auto", overflow: "visible" })
                }}
              >
                {subject ? (
                  /*
                   * The live region is cleared, not keyed, on a selection change: remounting it would
                   * drop the announcement, and a stale outcome would name the wrong user.
                   */
                  <>
                    <Box
                      sx={{
                        alignItems: "center",
                        borderBottom: `1px solid ${SURFACE_MUTED}`,
                        /*
                         * 8px below `md`, against 16px at desktop. The row is back + identity +
                         * actions on a 390px screen; a 16px gutter between each is space the values
                         * need (reviewer, 2026-09-23).
                         */
                        columnGap: SPACE_BLOCK,
                        display: "flex",
                        flexShrink: 0,
                        /*
                         * Wraps at both breakpoints, and below `md` that is what builds the app bar's
                         * three lines: `order` puts Back, the name and the actions first, then the
                         * account and the metadata line each take `width: 100%` and break. See
                         * `identityContentsOnMobile`.
                         */
                        flexWrap: "wrap",
                        /*
                         * **8px below `md`, 24px at desktop** (reviewer, 2026-09-23). 24px of inset on
                         * each side of a 390px viewport spends ~12% of the width on padding, and it is
                         * the single biggest reason the mobile header read as loose.
                         */
                        padding: SPACE_SECTION,
                        rowGap: SPACE_BLOCK,
                        ...belowMobile({ columnGap: "8px", padding: "8px" })
                      }}
                    >
                      {/*
                       * The mobile back affordance (TA4236901). Mobile-only because at desktop both
                       * panes are visible, so there is nothing to go back *from*.
                       *
                       * It is a real control rather than a reliance on the browser's Back button:
                       * TA4236901's own note is that "users on mobile will press the system back
                       * button before they find yours" — the fix for which is to make **both** work,
                       * which is what `useDrillDownView` does by putting the view in the location.
                       */}
                      {/*
                       * ⚠️ **The negative margin is optical alignment, not a hack — and the 48px
                       * target is preserved.**
                       *
                       * An Interstate icon `Button` at `size="lg"` is 48x48 with 14px of its own
                       * padding, so the chevron glyph sits 14px inside the button box. Added to the
                       * row's inset that put the glyph ~22px from the card edge while the name beside
                       * it started at 8px — the "too much space to its left" (reviewer, 2026-09-23).
                       *
                       * Pulling the wrapper left by the row's own 8px inset puts the *button box* flush
                       * to the card edge and the *glyph* ~14px in, which is where the eye expects it.
                       * The touch target is untouched at 48x48 — this moves the box, it does not shrink
                       * it, so `TOUCH_TARGET_MIN` still holds and the Cypress 44px assertion still
                       * passes.
                       */}
                      <Box
                        sx={{
                          /*
                           * **Back shares the name's line** — it briefly had `flexBasis: "100%"` to
                           * own a row of its own, which was reverted on 2026-09-23: a 48px row holding
                           * one glyph is a lot of vertical space on a phone, and giving the identity
                           * the full width did not stop it wrapping anyway. Truncating the name is
                           * what fixed that; see `IDENTITY_NAME_ON_MOBILE`.
                           */
                          ...mobileOnly("flex", {
                            marginLeft: "-8px",
                            order: 1
                          })
                        }}
                      >
                        <Button
                          analyticsConfiguration={{
                            eventName: "usermanager-back-to-list",
                            eventProperties: undefined
                          }}
                          aria-label={Locale.getText("BACK") as string}
                          buttonStyle={"tertiary"}
                          data-testid={"usermanager-back-to-list"}
                          icon={<ChevronLeftIcon title={""} />}
                          size={"lg"}
                          onClick={handleBackToList}
                        />
                      </Box>

                      {/*
                       * ⚠️ **This wrapper is `display: contents` below `md` and MUST stay that way.**
                       *
                       * `IdentityHeader` flattens itself on mobile so its name, account and metadata
                       * lines become direct flex items of the header row — that is the only way the
                       * last two can span the full width rather than being squeezed into the ~186px
                       * the identity gets between Back and the actions. A wrapper with any other
                       * `display` re-parents them to *itself* and the chain breaks silently: the
                       * layout simply reverts to a cramped column with no error anywhere.
                       *
                       * At desktop it is an ordinary flex item again — `flex: 1` takes the width Back
                       * and the actions do not, and `minWidth: 0` lets it fall below its content so
                       * the name can ellipsise.
                       */}
                      <Box
                        sx={{
                          /*
                           * Full width below `md` so the identity owns its own lines beneath the
                           * back/actions row; an ordinary flex item at desktop. `minWidth: 0` is what
                           * lets the name's ellipsis engage — a flex item's `min-width: auto` refuses
                           * to shrink below its content.
                           *
                           * An earlier revision made this `display: contents` to flatten the
                           * identity's lines into the header row. That was replaced: `IdentityHeader`
                           * now arranges its own three values with `flexWrap` + `order`, which is
                           * legible and cannot break from the outside.
                           */
                          flex: "1",
                          minWidth: 0,
                          order: 1,
                          ...belowMobile({ flex: "0 0 100%", order: 4 })
                        }}
                      >
                        <IdentityHeader subject={subject} />
                      </Box>

                      <Box
                        data-testid={"usermanager-header-actions"}
                        sx={{
                          /*
                           * `flex-start` below `md` so the cluster sits on the **name's** line rather
                           * than centring against the two-line identity block. `⋮` and Save share one
                           * container (`HeaderActions`), so they travel together — putting one per row
                           * would mean splitting that component, which is not worth it.
                           */
                          flexShrink: 0,
                          marginLeft: "auto",
                          /*
                           * Second on mobile, so it shares row one with Back and the identity's own
                           * lines fall beneath them both. `marginLeft: auto` pins the cluster right.
                           */
                          order: 2
                        }}
                      >
                        {/*
                         * `isSubjectSelected` is the literal `true`, not `Boolean(subject)`: this row
                         * renders inside the `subject ? (…)` branch, so there is always a subject by
                         * here. The false case is therefore unreachable from this page — the no-subject
                         * arm below shows `UM_NO_SELECTION` instead of a header of disabled controls.
                         * See `HeaderActions`' docstring before assuming that arm is covered.
                         */}
                        <HeaderActions
                          isSubjectSelected
                          accountId={accountId}
                          isDeleting={deleteUser.isPending}
                          isDirty={save.isFormDirty}
                          isSaving={save.isSaving}
                          isSubjectTheOperator={isSelfSelected}
                          subjectDisplayName={subject.displayName}
                          subjectUserId={subject.userId}
                          onDelete={handleDelete}
                          onSave={save.handleSave}
                        />
                      </Box>
                    </Box>

                    {/*
                     * Above the tabs so it shows from every tab; `role="alert"` is the announcement,
                     * so the live region is not written to as well.
                     */}
                    {save.saveFailureMessage ? (
                      <Box
                        sx={{
                          flexShrink: 0,
                          padding: `${SPACE_BLOCK} ${SPACE_SECTION} 0`,
                          ...belowMobile({ padding: "8px 8px 0" })
                        }}
                      >
                        <Alert
                          data-testid={"usermanager-save-error"}
                          role={"alert"}
                          type={"error"}
                        >
                          {save.saveFailureMessage}
                        </Alert>
                      </Box>
                    ) : null}

                    {/*
                     * FR-009/FR-055's notice is **not rendered here.** It renders inside each permission
                     * tab, conditioned on `authority` being absent — which is the same withholding this
                     * shell performs via `permissionAuthority`, so the explanation and the behavior come
                     * from one decision and cannot disagree.
                     *
                     * *This diverges from T080's literal wording ("pass no `authority` … and render
                     * `ReadOnlyNotice`"), deliberately.* The shipped tabs already rendered the notice
                     * unconditionally; making that conditional was necessary anyway, so rendering it here
                     * as well would show it twice. One source, at the tab.
                     */}

                    {/*
                     * The tab set. It stays inside the live region mounted above, so the region is
                     * mounted on page load and survives every tab switch — a region that mounts carrying
                     * its first message is the one least likely to be announced.
                     */}
                    <Box
                      sx={{
                        display: "flex",
                        flex: "1 1 auto",
                        flexDirection: "column",
                        minHeight: 0,
                        overflow: "hidden",
                        /*
                         * FR-005's second scroll region — **at desktop only.** Below `md` the panel
                         * grows to its content and the page scrolls once, so this inner scroller
                         * relaxes with the rest.
                         */
                        "& [role='tabpanel']": {
                          flex: "1 1 auto",
                          marginLeft: 0,
                          marginTop: 0,
                          minHeight: 0,
                          overflowY: "auto"
                        },
                        ...belowMobile({
                          minHeight: "auto",
                          overflow: "visible",
                          "& [role='tabpanel']": {
                            minHeight: "auto",
                            overflowY: "visible"
                          }
                        })
                      }}
                    >
                      <Tabs
                        ariaLabel={
                          Locale.getText("UM_TABS_ARIA_LABEL") as string
                        }
                        id={TABS_ID}
                        tabs={interstateTabs}
                        onActivate={selectTab}
                      />
                    </Box>
                  </>
                ) : (
                  /*
                   * FR-008. No subject is not a blank panel, and the list beside this stays fully usable
                   * so another user can be selected.
                   *
                   * **Only `"no-selection"` can reach here.** This branch used to distinguish the two
                   * reasons, but `"no-context"` is answered above — before the permission gate, which
                   * cannot be evaluated without an operator identity — so the other arm was unreachable
                   * and `tsc` flagged the comparison as having no overlap. Both states still exist and
                   * still say different things; they are just decided in different places now.
                   */
                  <Box
                    data-testid={"usermanager-no-selection"}
                    sx={{ padding: SPACE_SECTION }}
                  >
                    <Typography tag={"p"} variant={"body-md"}>
                      {Locale.getText("UM_NO_SELECTION") as string}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </StatusRegion>
        </Box>
      </Box>
    </UserInfoAreaContext.Provider>
  );
};
